import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Box } from './RobotTxtStyle';
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";


type SearchRobot = {
    name: string;
    action: 'Allow' | 'Disallow' | 'Same as Default';
};

const initialSearchRobots: SearchRobot[] = [
    { name: 'Google', action: 'Allow' },
    { name: 'Google Image', action: 'Allow' },
    { name: 'Google Mobile', action: 'Allow' },
    { name: 'MSN Search', action: 'Allow' },
    { name: 'Yahoo', action: 'Allow' },
    { name: 'Yahoo MM', action: 'Allow' },
    { name: 'Ask/Teoma', action: 'Allow' },
    { name: 'GigaBlast', action: 'Allow' },
    { name: 'DMOZ Checker', action: 'Allow' },
    { name: 'Nutch', action: 'Allow' },
    { name: 'Alexa/Wayback', action: 'Allow' },
    { name: 'Baidu', action: 'Allow' },
    { name: 'Naver', action: 'Allow' },
    { name: 'MSN PicSearch', action: 'Allow' },
];



const RobotsTxtGenerator: React.FC = () => {
    const [defaultAction, setDefaultAction] = useState<'Allowed' | 'Disallowed'>('Allowed');
    const [crawlDelay, setCrawlDelay] = useState<string>('Default');
    const [sitemap, setSitemap] = useState<string>('');
    const [searchRobots, setSearchRobots] = useState<SearchRobot[]>(initialSearchRobots);
    const [restrictedDirectories, setRestrictedDirectories] = useState<string[]>(['']);
    const [generatedContent, setGeneratedContent] = useState<string>('');

    useEffect(() => {
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: 'setHeight', height }, '*');
    }, [generatedContent, defaultAction, crawlDelay, sitemap, searchRobots, restrictedDirectories]);

    const addDirectory = () => {
        setRestrictedDirectories([...restrictedDirectories, '']);
    };

    const removeDirectory = (index: number) => {
        const newDirs = restrictedDirectories.filter((_, i) => i !== index);
        setRestrictedDirectories(newDirs);
    };

    const updateDirectory = (index: number, value: string) => {
        const newDirs = [...restrictedDirectories];
        newDirs[index] = value;
        setRestrictedDirectories(newDirs);
    };

    const generateRobotsTxt = () => {
        let content = "# robots.txt generated by Coozmoo\n";
        content += "User-agent: *\n";
        restrictedDirectories.forEach((dir) => {
            if (dir.trim() !== '') {
                content += `Disallow: ${dir}\n`;
            }
        });
        if (crawlDelay !== 'Default') {
            content += `Crawl-delay: ${crawlDelay}\n`;
        }
        if (sitemap.trim() !== '') {
            content += `Sitemap: ${sitemap}\n`;
        }
        searchRobots.forEach((robot) => {
            if (robot.action !== 'Same as Default') {
                content += `User-agent: ${robot.name}\n${robot.action}: /\n`;
            }
        });
        return content;
    };

    const handleGenerate = () => {
        const content = generateRobotsTxt();
        setGeneratedContent(content);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedContent).then(() => {
            alert('Copied to clipboard!');
        });
    };

    const downloadFile = () => {
        const element = document.createElement('a');
        const file = new Blob([generatedContent], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'robots.txt';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const handleReset = () => {
        setDefaultAction('Allowed');
        setCrawlDelay('Default');
        setSitemap('');
        setSearchRobots(initialSearchRobots);
        setRestrictedDirectories(['']);
        setGeneratedContent('');
    };

    return (
        <Box className={`${!isInIframe ? "py-5 defaultText" : "py-3 interDisplay"}`} style={{ background: "#ffffff" }}>
            {!isInIframe && (
                <ToolsHeader
                    title="Robots.txt Generator"
                    description="Generate a search-engine friendly robots.txt file with full control over crawlers, directories, crawl-delay, and sitemap."
                />
            )}
            <Card className="container tool-card">
                <Card.Body>
                    <Form>
                        <Form.Group className="mb-3 form-row">
                            <Form.Label>Default - All Robots are:</Form.Label>
                            <Form.Select
                                value={defaultAction}
                                onChange={(e) => setDefaultAction(e.target.value as 'Allowed' | 'Disallowed')}
                            >
                                <option value="Allowed">Allowed</option>
                                <option value="Disallowed">Disallowed</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3 form-row">
                            <Form.Label>Crawl-Delay:</Form.Label>
                            <Form.Select value={crawlDelay} onChange={(e) => setCrawlDelay(e.target.value)}>
                                <option value="Default">Default - No Delay</option>
                                <option value="5">5s</option>
                                <option value="10">10s</option>
                                <option value="60">60s</option>
                                <option value="120">120s</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3 form-row">
                            <Form.Label>Sitemap (leave blank if you don't have):</Form.Label>
                            <Form.Control
                                type="url"
                                value={sitemap}
                                onChange={(e) => setSitemap(e.target.value)}
                                placeholder="https://example.com/sitemap.xml"
                            />
                        </Form.Group>

                        <h3>Search Robots</h3>
                        <Row className="grid-container">
                            {searchRobots.map((robot, index) => (
                                <Col key={robot.name} className="grid-item">
                                    <Form.Group className="mb-3">
                                        <Form.Label>{robot.name}</Form.Label>
                                        <Form.Select
                                            value={robot.action}
                                            onChange={(e) => {
                                                const newAction = e.target.value as 'Allow' | 'Disallow' | 'Same as Default';
                                                setSearchRobots((prev) => {
                                                    const newRobots = [...prev];
                                                    newRobots[index].action = newAction;
                                                    return newRobots;
                                                });
                                            }}
                                        >
                                            <option value="Allow">Allowed</option>
                                            <option value="Disallow">Disallowed</option>
                                            <option value="Same as Default">Same as Default</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>

                        <Form.Group className="mb-3 form-row">
                            <Form.Label>Restricted Directories:</Form.Label>
                            <div id="restricted_directories_container">
                                {restrictedDirectories.map((dir, index) => (
                                    <div key={index} className="restricted-directory-item d-flex mb-2">
                                        <Form.Control
                                            type="text"
                                            value={dir}
                                            onChange={(e) => updateDirectory(index, e.target.value)}
                                            placeholder="/cgi-bin/"
                                            className="restricted-directory-input me-2"
                                        />
                                        {index > 0 && (
                                            <Button
                                                variant="danger"
                                                className="remove-directory-btn me-2"
                                                onClick={() => removeDirectory(index)}
                                            >
                                                -
                                            </Button>
                                        )}
                                        {index === restrictedDirectories.length - 1 && (
                                            <Button variant="success" className="add-directory-btn" onClick={addDirectory}>
                                                +
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Form.Group>

                        <div className="button-row">
                            <Button className="generate-btn" onClick={handleGenerate}>
                                Create Robots.txt
                            </Button>
                            <Button className="reset-btn" onClick={handleReset}>
                                Reset
                            </Button>
                        </div>
                    </Form>

                    {generatedContent && (
                        <div className="result-section mt-4">
                            <h3>Generated Robots.txt Content:</h3>
                            <pre className="result-box">{generatedContent}</pre>
                            <div className="button-row">
                                <Button className="copy-btn" onClick={copyToClipboard}>
                                    Copy to Clipboard
                                </Button>
                                <Button className="download-btn" onClick={downloadFile}>
                                    Download robots.txt
                                </Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>


        </Box>
    );
};

export default RobotsTxtGenerator;